/**
 * DanismanSatislariPanels.jsx
 *
 * Danışman CRM — "Danışman Satışları" panelleri. İki sayfa tarafından
 * paylaşılır:
 *   - AdminCiroDashboard.jsx (Faz 1 — /admin/ciro-dashboard)
 *   - AdminDanismanAnaSayfa.jsx (Faz 2 — /danisman, Blok 1)
 *
 * Kod tekrarını önlemek için buraya taşındı.
 *
 * 2026-08 kırıcı backend değişikliği: `GET .../danisman-satislari` artık
 * ROL FARK ETMEKSİZİN aynı şekli döner — `{ satislar: [...] }` (tüm
 * danışmanların tutara göre azalan sıralı satırları, `kendi` bayrağı ve
 * danışman rolünde başkalarının satırında `toplam_tutar`/`oran_yuzde` alanları
 * `null`, `satis_adedi` her zaman dolu). Eski tekil `satis` objesi KALDIRILDI.
 * Bu yüzden `DanismanSatislariCards` artık ROL AYRIMI OLMADAN hem admin hem
 * danışman rolünde kullanılır — kısmi maskeleme sunucuda yapılır, burada
 * yalnızca `null` alanları "—" ile ve `kendi === true` satırını görsel
 * vurguyla gösteririz (bkz. bileşen içi yorum). Eski "Günlük/Aylık Cironuz"
 * özet kartları (`ConsultantRevenueCards`, `ConsultantPerformancePanel`)
 * tekil `satis` objesine bağımlıydı, kaldırıldı. Prim/Hakediş kartı ayrı bir
 * endpoint kullandığı için (`/danisman/hakedis`) etkilenmedi, `HakedisPanel`
 * adıyla bağımsız bir bileşene ayrıştırıldı (yalnız danışman rolünde,
 * AdminCiroDashboard.jsx render eder).
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { fmtMoney, fmtPercent } from '../../utils/money';
import { Skeleton } from '../ui/skeleton';
import HakedisPenceresiRozeti from '../admin/HakedisPenceresiRozeti';
import HakedisDetayTablo from '../admin/HakedisDetayTablo';
import { RefreshCw, Award, ChevronDown } from 'lucide-react';
import { NavControl, todayStr, currentMonthStr, shiftDate, shiftMonth } from './CrmKpiPrimitives';
import { isBosSatir, tahsilatBekliyorMu } from '../../lib/danismanSatislariHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Ortak: "kendi" satırı rozeti + tutar/oran null gösterimi ──────────────

/** toplam_tutar/oran_yuzde null ise (danışman rolünde başkasının satırı,
 * sunucu tarafı kısmi maskeleme) "—" döner — 0 GÖSTERMEZ, fmtMoney(null)
 * yanıltıcı biçimde "₺0" döndürür, o yüzden burada elle kontrol edilir. */
function fmtTutarOrDash(v) {
  return v === null || v === undefined ? '—' : fmtMoney(v);
}
function fmtOranOrDash(v, lang) {
  return v === null || v === undefined ? '—' : fmtPercent(v, lang);
}

/** Kendi satırını vurgulamak için metin etiketi + arka plan — renk TEK
 * bilgi taşıyıcısı olmasın diye "(siz)"/"(you)" etiketi şart (bkz. görev). */
function KendiRozeti({ tr }) {
  return (
    <span
      className="px-1.5 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0"
      style={{ backgroundColor: 'rgba(46,107,143,0.14)', color: 'var(--crm-info)' }}
    >
      {tr('(siz)', '(you)')}
    </span>
  );
}

// isBosSatir / tahsilatBekliyorMu artık `../../lib/danismanSatislariHelpers`
// içinde (F-K1 — saf fonksiyon olarak izole edildi, bkz. o dosyanın başındaki
// yorum ve `src/__tests__/crm/DanismanSatislariPanels.test.js`).

// ─── Danışman Satışları (tüm danışmanlar — sıralı liste; hem admin hem
// danışman rolünde AYNI şekilde render edilir, kısmi maskeleme sunucudan
// gelir) ─────────────────────────────────────────────────────────────────

/**
 * showTahsilat (24 Ağustos 2026 — Recep kararı):
 *   true  (VARSAYILAN) → Ciro Dashboard'daki tam görünüm; "Tahsil Edilen" ve
 *                        "Tahsilat Payı %" kolonları ve günlük karttaki
 *                        tahsilat rakamı GÖSTERİLİR. Davranış değişmedi.
 *   false → Danışman ANA SAYFA görünümü; panel baştan sona CİRO odaklı olur,
 *           tahsilat hiç gösterilmez. Gerekçe: ana sayfa kartı "bugün/bu ay ne
 *           sattım" sorusunu cevaplıyor; vadeli satışta tahsilat 0 olduğu için
 *           danışman satışını yapmış olmasına rağmen "yok" görüyordu.
 *           Tahsilat kırılımı Ciro Dashboard'da duruyor.
 * Backend HER İKİ alanı da döndürmeye devam eder — bu yalnızca görünüm
 * kararıdır, API sözleşmesi değişmedi.
 */
export function DanismanSatislariCards({ authHeaders, lang, tr, showTahsilat = true }) {
  const [date, setDate] = useState(todayStr());
  const [month, setMonth] = useState(currentMonthStr());
  const [gunlukData, setGunlukData] = useState(null);
  const [aylikData, setAylikData] = useState(null);
  const [gLoading, setGLoading] = useState(false);
  const [aLoading, setALoading] = useState(false);
  const [gError, setGError] = useState(false);
  const [aError, setAError] = useState(false);
  const toast = useToast();

  const fetchGunluk = useCallback(async () => {
    setGLoading(true); setGError(false);
    try {
      const r = await axios.get(`${API}/danisman/ciro-dashboard/danisman-satislari?date=${date}`, { headers: authHeaders() });
      setGunlukData(r.data.satislar || []);
    } catch (err) { setGError(true); toast.error(getApiErrorMessage(err, lang)); }
    finally { setGLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, lang]);

  const fetchAylik = useCallback(async () => {
    setALoading(true); setAError(false);
    try {
      const r = await axios.get(`${API}/danisman/ciro-dashboard/danisman-satislari?month=${month}`, { headers: authHeaders() });
      setAylikData(r.data.satislar || []);
    } catch (err) { setAError(true); toast.error(getApiErrorMessage(err, lang)); }
    finally { setALoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, lang]);

  useEffect(() => { fetchGunluk(); }, [fetchGunluk]);
  useEffect(() => { fetchAylik(); }, [fetchAylik]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
      {/* Günlük */}
      <div className="bg-white p-4" style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-sm" style={{ color: 'var(--crm-navy-text)' }}>{tr('Günlük Danışman Satışları', 'Daily Consultant Sales')}</h3>
          <NavControl
            value={date}
            onPrev={() => setDate((d) => shiftDate(d, -1))}
            onNext={() => setDate((d) => shiftDate(d, 1))}
            nextDisabled={date >= todayStr()}
            tr={tr}
          />
        </div>
        {gLoading && <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}</div>}
        {!gLoading && gError && (
          <button onClick={fetchGunluk} className="text-xs font-bold flex items-center gap-1" style={{ color: 'var(--crm-danger)' }}>
            <RefreshCw size={12} /> {tr('Tekrar Dene', 'Retry')}
          </button>
        )}
        {!gLoading && !gError && (gunlukData || []).length === 0 && (
          <p className="text-xs" style={{ color: 'var(--crm-muted)' }}>{tr('Bu gün için satış yok.', 'No sales for this day.')}</p>
        )}
        {!gLoading && !gError && (gunlukData || []).length > 0 && (() => {
          // Günlük kart: `yapilan_ciro` backend'de henüz olmayabilir (paralel
          // geliştirme). Veri setinde HİÇ yoksa ek kolon uydurmuyoruz — sadece
          // en az bir satırda gerçekten geldiyse ikinci rakamı gösteriyoruz.
          const gunlukYapilanCiroVar = gunlukData.some((r) => r.yapilan_ciro !== undefined);
          return (
            <div className="space-y-1.5">
              {gunlukData.filter((r) => r.danisman_id).map((r, i) => {
                const bosSatir = isBosSatir(r);
                return (
                  <div
                    key={r.danisman_id}
                    className={`flex items-center gap-2 py-1.5 px-2 rounded-lg text-sm ${bosSatir ? 'opacity-60' : ''}`}
                    style={{
                      backgroundColor: r.kendi && !bosSatir ? 'rgba(46,107,143,0.08)' : 'var(--crm-cream)',
                      border: bosSatir ? '1px dashed var(--crm-card-border)' : (r.kendi ? '1px solid rgba(46,107,143,0.25)' : '1px solid transparent'),
                    }}
                  >
                    <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{ backgroundColor: 'var(--crm-navy)' }}>{i + 1}</span>
                    <span className="flex-1 font-semibold truncate" style={{ color: 'var(--crm-navy-text)' }}>{r.danisman_adi}</span>
                    {r.kendi && <KendiRozeti tr={tr} />}
                    {bosSatir ? (
                      <span className="text-xs font-semibold italic flex-shrink-0" style={{ color: 'var(--crm-muted-2)' }}>
                        {tr('Henüz satışınız yok', 'No sales yet')}
                      </span>
                    ) : gunlukYapilanCiroVar ? (
                      <span className="flex items-center gap-2 flex-shrink-0">
                        <span className="flex flex-col items-end leading-tight">
                          <span className="text-[9px] font-semibold" style={{ color: 'var(--crm-muted-2)' }}>{tr('Ciro', 'Revenue')}</span>
                          <span className="text-xs font-bold" style={{ color: 'var(--crm-navy-text)' }}>{fmtTutarOrDash(r.yapilan_ciro)}</span>
                        </span>
                        {showTahsilat && (
                          <span className="flex flex-col items-end leading-tight">
                            <span className="text-[9px] font-semibold" style={{ color: 'var(--crm-muted-2)' }}>{tr('Tahsilat', 'Collected')}</span>
                            <span className="text-xs font-bold" style={{ color: 'var(--crm-navy-text)' }}>{fmtTutarOrDash(r.toplam_tutar)}</span>
                            {tahsilatBekliyorMu(r) && (
                              <span className="text-[9px] font-semibold italic text-amber-600">{tr('tahsilat bekliyor', 'awaiting collection')}</span>
                            )}
                          </span>
                        )}
                      </span>
                    ) : (
                      /* Geri donus dali: backend `yapilan_ciro` gondermiyorsa.
                         showTahsilat=false iken (ana sayfa) tahsilat rakamini
                         buradan da sizdirmiyoruz — "—" gosterilir. */
                      <span className="text-xs font-bold flex-shrink-0" style={{ color: 'var(--crm-navy-text)' }}>
                        {showTahsilat ? fmtTutarOrDash(r.toplam_tutar) : '—'}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* Aylık */}
      <div className="bg-white p-4" style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-sm" style={{ color: 'var(--crm-navy-text)' }}>{tr('Aylık Danışman Satışları', 'Monthly Consultant Sales')}</h3>
          <NavControl
            value={month}
            onPrev={() => setMonth((m) => shiftMonth(m, -1))}
            onNext={() => setMonth((m) => shiftMonth(m, 1))}
            nextDisabled={month >= currentMonthStr()}
            tr={tr}
          />
        </div>
        {aLoading && <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}</div>}
        {!aLoading && aError && (
          <button onClick={fetchAylik} className="text-xs font-bold flex items-center gap-1" style={{ color: 'var(--crm-danger)' }}>
            <RefreshCw size={12} /> {tr('Tekrar Dene', 'Retry')}
          </button>
        )}
        {!aLoading && !aError && (aylikData || []).length === 0 && (
          <p className="text-xs" style={{ color: 'var(--crm-muted)' }}>{tr('Bu ay için satış yok.', 'No sales for this month.')}</p>
        )}
        {!aLoading && !aError && (aylikData || []).length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ color: 'var(--crm-muted-2)' }} className="text-left">
                  <th scope="col" className="pb-1.5 font-bold">#</th>
                  <th scope="col" className="pb-1.5 font-bold">{tr('Danışman', 'Consultant')}</th>
                  {/* PLAN §2.7: "Yapılan Ciro" (düzenlenen satış sözleşmesi
                      bedeli — tahsil edilmiş olması gerekmez) ile "Tahsil
                      Edilen" (fiilen kasaya giren, eski "Tutar") ayrı
                      kolonlar. Backend alanı henüz yoksa hücre "—" gösterir
                      (fmtTutarOrDash), kolon yine de eklenir — aylık için
                      şart koşulsuz (görev tanımı). */}
                  <th scope="col" className="pb-1.5 font-bold text-right">{tr('Yapılan Ciro', 'Revenue Made')}</th>
                  {showTahsilat && (
                  <th scope="col" className="pb-1.5 font-bold text-right">{tr('Tahsil Edilen', 'Collected')}</th>
                  )}
                  {/* G1 duzeltmesi: bu metrik "tahsil edilen / yapilan ciro"
                      DEGIL — paydasi TUM danismanlarin toplam cirosu
                      (backend/routes/ciro_dashboard.py:289-303,
                      grand_total = tum danismanlarin toplam_tutar toplami;
                      ayni dosyada :693-699 "kasitli ve kullanici onayli"
                      diye belgelenmis). Yani bu, danismanin toplam tahsilat
                      icindeki PAYI (market share), satirdaki tahsilat oranı
                      DEGIL. Eski etiket ("Oran % (tahsilat)") "Yapilan
                      Ciro"/"Tahsil Edilen" kolonlarinin hemen yaninda
                      durdugu icin okuyucuyu bu iki kolonun oranini
                      gosterdigine inandiriyordu — hesap dogru, etiket
                      yanlisti. */}
                  {showTahsilat && (
                  <th
                    scope="col"
                    className="pb-1.5 font-bold text-right"
                    title={tr(
                      'Bu danışmanın tahsilatının, tüm danışmanların toplam cirosu içindeki payı.',
                      "This consultant's collections as a share of all consultants' total revenue."
                    )}
                  >
                    {tr('Tahsilat Payı %', 'Share of Collections %')}
                  </th>
                  )}
                  <th
                    scope="col"
                    className="pb-1.5 font-bold text-right"
                    title={tr(
                      'Satilan egitim sayisi. Ana egitim ve yanindaki her ek egitim ayri sayilir.',
                      'Number of courses sold. The main course and each extra course count separately.'
                    )}
                  >
                    {tr('Adet', 'Count')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {aylikData.filter((r) => r.danisman_id).map((r, i) => {
                  const bosSatir = isBosSatir(r);
                  return (
                    <tr
                      key={r.danisman_id}
                      className={`border-t ${bosSatir ? 'opacity-60' : ''}`}
                      style={{ borderColor: 'var(--crm-card-border)', backgroundColor: !bosSatir && r.kendi ? 'rgba(46,107,143,0.08)' : undefined }}
                    >
                      <td className="py-1.5 font-bold" style={{ color: 'var(--crm-navy-text)' }}>{i + 1}</td>
                      <td className="py-1.5 font-semibold" style={{ color: 'var(--crm-navy-text)' }}>
                        <span className="inline-flex items-center gap-1.5">
                          {r.danisman_adi}
                          {r.kendi && <KendiRozeti tr={tr} />}
                        </span>
                      </td>
                      {bosSatir ? (
                        /* colSpan basliklarla ayni sayida olmali: tahsilat
                           kolonlari gizliyken 4 degil 2 hucre var. */
                        <td colSpan={showTahsilat ? 4 : 2} className="py-1.5 text-right italic font-semibold" style={{ color: 'var(--crm-muted-2)' }}>
                          {tr('Henüz satışınız yok', 'No sales yet')}
                        </td>
                      ) : (
                        <>
                          <td className="py-1.5 text-right font-bold" style={{ color: 'var(--crm-navy-text)' }}>{fmtTutarOrDash(r.yapilan_ciro)}</td>
                          {showTahsilat && (
                            <td className="py-1.5 text-right font-bold" style={{ color: 'var(--crm-navy-text)' }}>
                              <div className="flex flex-col items-end leading-tight">
                                <span>{fmtTutarOrDash(r.toplam_tutar)}</span>
                                {tahsilatBekliyorMu(r) && (
                                  <span className="text-[9px] font-semibold italic text-amber-600">{tr('tahsilat bekliyor', 'awaiting collection')}</span>
                                )}
                              </div>
                            </td>
                          )}
                          {showTahsilat && (
                            <td className="py-1.5 text-right font-bold" style={{ color: 'var(--crm-gold-dark)' }}>{fmtOranOrDash(r.oran_yuzde, lang)}</td>
                          )}
                          {/* Adet = SATILAN KURS sayisi (kurs_adedi), tahsilat
                              adedi (satis_adedi) DEGIL. Ikisi farkli evrenden
                              gelir: satis_adedi tahsilat kayitlarindaki tekil
                              fatura sayisiydi, bu yuzden vadeli satis yapip
                              henuz tahsilati olmayan danismanda "Yapilan Ciro
                              64.447,99 / Adet 0" gibi celiskili bir satir
                              cikiyordu (25 Agustos 2026, Recep bildirdi).
                              kurs_adedi ile ayni evrenden gelir: yapilan
                              ciroyu ureten faturalarin kurs kalemleri.
                              ?? fallback: backend eski surumdeyse (alan yok)
                              eski davranisa duser, satir bos gorunmez. */}
                          <td className="py-1.5 text-right" style={{ color: 'var(--crm-muted)' }}>{r.kurs_adedi ?? r.satis_adedi}</td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Prim / Hakediş Paneli (yalnız danışman rolü — Ciro Dashboard) ─────────
//
// Eskiden ConsultantPerformancePanel'in 3. kartıydı (Günlük/Aylık Ciro
// kartlarıyla birlikte); satış kartları artık `DanismanSatislariCards`
// (maskeleme destekli paylaşılan liste) ile karşılandığı için buraya
// bağımsız bir bileşen olarak ayrıştırıldı. `/danisman/hakedis` endpoint'i
// bu değişimden ETKİLENMEDİ — davranış aynen korunur.

export function HakedisPanel({ authHeaders, lang, tr }) {
  const [month, setMonth] = useState(currentMonthStr());
  const [hakedis, setHakedis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const toast = useToast();

  const fetchHakedis = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const r = await axios.get(`${API}/danisman/hakedis?donem=${month}`, { headers: authHeaders() });
      setHakedis(r.data || null);
    } catch (err) { setError(true); toast.error(getApiErrorMessage(err, lang)); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, lang]);

  useEffect(() => { fetchHakedis(); }, [fetchHakedis]);

  const oranEfektif = hakedis && hakedis.donem_tahsilat_toplami > 0
    ? (hakedis.hakedis_toplam / hakedis.donem_tahsilat_toplami) * 100
    : 0;

  return (
    <div className="bg-white p-4 mb-5" style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1.5">
        <h3 className="font-bold text-sm flex items-center gap-1.5" style={{ color: 'var(--crm-navy-text)' }}>
          <Award size={14} style={{ color: 'var(--crm-gold-dark)' }} /> {tr('Prim / Hakediş', 'Commission / Earnings')}
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <HakedisPenceresiRozeti tr={tr} lang={lang} donem={month} kuralVar={hakedis?.kural_var !== false} />
          <NavControl value={month} onPrev={() => setMonth((m) => shiftMonth(m, -1))} onNext={() => setMonth((m) => shiftMonth(m, 1))} nextDisabled={month >= currentMonthStr()} tr={tr} />
        </div>
      </div>
      {loading && (<><Skeleton className="h-4 w-1/2 mb-2" /><Skeleton className="h-8 w-2/3" /></>)}
      {!loading && error && (
        <button onClick={fetchHakedis} className="text-xs font-bold flex items-center gap-1" style={{ color: 'var(--crm-danger)' }}>
          <RefreshCw size={12} /> {tr('Tekrar Dene', 'Retry')}
        </button>
      )}
      {!loading && !error && hakedis && hakedis.kural_var === false && (
        <p className="text-xs font-semibold" style={{ color: 'var(--crm-muted)' }}>
          {tr('Prim kuralı henüz tanımlanmadı. Yöneticinizle iletişime geçin.', 'No commission rule has been defined yet. Please contact your manager.')}
        </p>
      )}
      {!loading && !error && hakedis && hakedis.kural_var !== false && (
        <>
          <p className="text-2xl font-bold" style={{ color: 'var(--crm-gold-dark)' }}>{fmtMoney(hakedis?.hakedis_toplam)}</p>
          <p className="text-xs font-semibold mt-1" style={{ color: 'var(--crm-muted)' }}>
            {tr('Ciron', 'Your revenue')}: {fmtMoney(hakedis?.donem_tahsilat_toplami)} · {tr('Oran', 'Rate')}: ≈{fmtPercent(oranEfektif, lang)}
          </p>
          {(hakedis?.kademe_detay?.length > 0 || hakedis?.detay?.length > 0 || hakedis?.pencere_disi_tahsilat > 0) && (
            <>
              <button
                type="button"
                onClick={() => setShowDetail((v) => !v)}
                className="mt-2 text-[11px] font-bold flex items-center gap-1"
                style={{ color: 'var(--crm-info)' }}
              >
                <ChevronDown size={12} className={showDetail ? 'rotate-180 transition-transform' : 'transition-transform'} />
                {showDetail ? tr('Detayı Gizle', 'Hide Details') : tr('Detayı Göster', 'Show Details')}
              </button>
              {showDetail && (
                <HakedisDetayTablo
                  kademeDetay={hakedis.kademe_detay}
                  detay={hakedis.detay}
                  pencereDisiTahsilat={hakedis.pencere_disi_tahsilat}
                  tr={tr}
                  lang={lang}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
